import {
  Box,
  Button,
  Checkbox,
  Group,
  Loader,
  LoadingOverlay,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { JSX, useEffect, useState } from "react";
import { formatDate } from "../../utils/CommonUtil";
import {
  getBaselineTests,
  getTestListLean,
  setBaselineTests,
} from "../../utils/api-communication/RestApiCli";
import { useTranslation } from "react-i18next";
import useFormComponentsData from "../new_test_ui/new_test_steps/data/FormComponentsData";
import { displayClinicianLabel } from "@utils/clinicianLabelUtils";
import { ClinicianLabelField } from "types/tests";

type SelectableTestListProps = {
  title?: string | JSX.Element;
  description?: string | JSX.Element;
  patientId?: string;
  selectable?: boolean;
  baselinSet?: boolean;
  generateButton?: (selectedRows: string[], baselineTests?: string[]) => void;
  isGenerateDisabled?: (selectedRows: string[]) => boolean;
  testTypeFilter?: string;
  testBehaviourFilter?: string;
  defaultEye?: string;
  filterable?: boolean;
  alwaysShowFilter?: boolean;
  selectionLimit?: number;
  showSelectedOnTop?: boolean;
  defaultTestType?: string;
  ascending?: boolean;
  showAfterBaseline?: boolean;
  setDefaultSelectedTestsOnStart?: (tests: any[]) => string[];
  minimumTestNeededWarningText?: string;
  minimumTestNeeded?: number;
};

export default function SelectableTestList(props: SelectableTestListProps) {
  const { t } = useTranslation();
  const [selectedRows, setSelectedRows] = useState([]);
  const [page, setPage] = useState(1);
  const [tests, setTests] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [showFilter, setShowFilter] = useState(false);
  const [showAllColumns, setShowAllColumns] = useState(false);

  const { testBehaviourData, testTypeData } = useFormComponentsData();

  const [testType, setTestType] = useState(null);
  const [testBehaviour, setTestBehaviour] = useState(null);
  const [eye, setEye] = useState(props.defaultEye || null);

  const [testTypeFilter, setTestTypeFilter] = useState(null);
  const [testBehaviourFilter, setTestBehaviourFilter] = useState(null);
  const [testTypeDataFiltered, setTestTypeDataFiltered] = useState(null);
  const [testBehaviourDataFiltered, setTestBehaviourDataFiltered] = useState(null);
  const [buttonLoading, setButtonLoading] = useState(false);

  const [defaultSelected, setDefaultSelected] = useState(null);
  const [baselineTestIdsFromCloud, setBaselineTestIdsFromCloud] = useState(null);
  const [loading, setLoading] = useState(false);

  const normalizeSelectDataValues = (data) => {
    if (!Array.isArray(data)) return data;
    return data.map((group) => ({
      ...group,
      items: Array.isArray(group.items)
        ? group.items.map((item) => ({
            ...item,
            value: String(item.value),
          }))
        : group.items,
    }));
  };

  const filterTestTypeData = (filterList, data) => {
    const filteredData = data
      .map((group) => {
        const filteredItems = group.items.filter((item) => filterList.includes(String(item.value)));
        if (filteredItems.length > 0) {
          return {
            group: group.group,
            items: filteredItems,
          };
        }
        return null;
      })
      .filter((group) => group !== null);
    return filteredData;
  };

  function getBaseline() {
    getBaselineTests({ testType: testType, eye: eye, patientId: props.patientId }, (res) => {
      const defaults = res.data;
      if (defaults.length == 2) {
        setSelectedRows(defaults);
        setDefaultSelected(defaults);
      } else {
        const ids = [tests[0].uuid, tests[1].uuid];
        setDefaultSelected(ids);
        setSelectedRows(ids);
      }
    });
  }

  useEffect(() => {
    if (props.defaultTestType) {
      setTestType(props.defaultTestType);
    }
    if (props.defaultEye) {
      setEye(props.defaultEye);
    }
    if (props.testTypeFilter) {
      setTestTypeFilter(props.testTypeFilter);
    }
    if (props.testBehaviourFilter) {
      setTestBehaviourFilter(props.testBehaviourFilter);
    }
  }, [props.defaultEye]);

  useEffect(() => {
    if (
      props.patientId &&
      props.showSelectedOnTop &&
      testType &&
      eye &&
      tests.length > 1 &&
      !defaultSelected
    ) {
      getBaseline();
    }
  }, [props.patientId, tests]);

  useEffect(() => {
    if (
      props.patientId &&
      props.setDefaultSelectedTestsOnStart &&
      testType &&
      eye &&
      tests.length > 1 &&
      !defaultSelected
    ) {
      const ids = props.setDefaultSelectedTestsOnStart(tests);
      setDefaultSelected(ids);
      setSelectedRows(ids);
    }
  }, [props.patientId, tests]);

  useEffect(() => {
    setDefaultSelected(null);
  }, [testType, testBehaviour, eye]);

  useEffect(() => {
    setTestTypeDataFiltered(
      normalizeSelectDataValues(
        testTypeFilter ? filterTestTypeData(testTypeFilter.split(","), testTypeData) : testTypeData,
      ),
    );
    setTestBehaviourDataFiltered(
      normalizeSelectDataValues(
        testBehaviourFilter
          ? filterTestTypeData(testBehaviourFilter.split(","), testBehaviourData)
          : testBehaviourData,
      ),
    );
  }, [testTypeFilter, testBehaviourFilter]);

  useEffect(() => {
    if (!props.patientId) {
      return;
    }
    setLoading(true);
    const query = {
      patientId: props.patientId ? props.patientId : null,
      limit: 10,
      page: page,
    };
    if (props.ascending) {
      query["ascending"] = props.ascending;
    }
    if (testType) {
      query["testType"] = testType;
    } else if (testTypeFilter) {
      query["testType"] = testTypeFilter;
    }
    if (testBehaviour) {
      query["testBehaviour"] = Number.isNaN(Number(testBehaviour))
        ? testBehaviour
        : Number(testBehaviour);
    } else if (testBehaviourFilter) {
      query["testBehaviour"] = testBehaviourFilter;
    }
    if (eye) query["eye"] = eye;

    if (props.showAfterBaseline) {
      query["afterBaseline"] = true;
    }

    getTestListLean(
      query,
      (res) => {
        const tests = res.data.data;
        setTests(tests);
        setTotalPages(res.data.lastPage);
        if (res.data.baselineTestIds) {
          setBaselineTestIdsFromCloud(res.data.baselineTestIds);
        }
        setLoading(false);
      },
      (error) => {
        console.error(error);
        setLoading(false);
      },
    );
    setSelectedRows([]);
  }, [
    props.patientId,
    page,
    testBehaviour,
    testType,
    eye,
    testBehaviourDataFiltered,
    testTypeDataFiltered,
    testTypeFilter,
    testBehaviourFilter,
  ]);

  const table = (testsList) => (
    <Table highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          {props.selectable && (
            <Table.Th>
              <Button
                variant="outline"
                radius="lg"
                size="compact-xs"
                onClick={() => {
                  setSelectedRows([]);
                }}
                disabled={selectedRows.length === 0}
              >
                {t("OpaReports.clear")}
              </Button>
            </Table.Th>
          )}
          <Table.Th>
            <Text fw={600}>{t("OpaReports.date")}</Text>
          </Table.Th>
          <Table.Th>
            <Text fw={600}>{t("OpaReports.type")}</Text>
          </Table.Th>
          <Table.Th>
            <Text fw={600}>{t("OpaReports.style")}</Text>
          </Table.Th>
          <Table.Th>
            <Text fw={600}>{t("OpaReports.eye")}</Text>
          </Table.Th>
          {showAllColumns && (
            <>
              <Table.Th>
                <Text fw={600}>{t("ClinicianLabel.diagnosis")}</Text>
              </Table.Th>
              <Table.Th>
                <Text fw={600}>{t("ClinicianLabel.reliability")}</Text>
              </Table.Th>
            </>
          )}
          <Table.Th></Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>{rows(testsList)}</Table.Tbody>
    </Table>
  );

  const rows = (testsList) =>
    testsList.map((element) => (
      <Table.Tr
        key={element.uuid}
        style={{
          cursor: "pointer",
          ...(selectedRows.includes(element.uuid)
            ? { backgroundColor: "var(--mantine-color-blue-light)" }
            : undefined),
        }}
        onClick={() => {
          if (props.selectable) {
            if (
              props.selectionLimit &&
              selectedRows.length >= props.selectionLimit &&
              !selectedRows.includes(element.uuid)
            ) {
              return;
            }
            setSelectedRows(
              selectedRows.includes(element.uuid)
                ? selectedRows.filter((position) => position !== element.uuid)
                : [...selectedRows, element.uuid],
            );
          } else {
            window.open(window.location.origin + "/test-result?uuid=" + element.uuid, "_blank");
          }
        }}
      >
        {props.selectable && (
          <Table.Td>
            <Checkbox
              disabled={
                props.selectionLimit &&
                selectedRows.length >= props.selectionLimit &&
                !selectedRows.includes(element.uuid)
              }
              style={{ cursor: "pointer" }}
              aria-label="Select row"
              checked={selectedRows.includes(element.uuid)}
              onChange={(event) =>
                setSelectedRows(
                  event.currentTarget.checked
                    ? [...selectedRows, element.uuid]
                    : selectedRows.filter((position) => position !== element.uuid),
                )
              }
            />
          </Table.Td>
        )}
        <Table.Td>
          <Text>{formatDate(element.startDate)}</Text>
        </Table.Td>
        <Table.Td>
          <Text>
            {element.category && element.category !== "VFA"
              ? t("TestType." + element.category)
              : t("TestType." + element.testType, element.testType as string)}
          </Text>
        </Table.Td>
        <Table.Td>
          <Text>
            {element.category && element.category !== "VFA"
              ? t("TestType." + element.testType)
              : t("TestController.testBehaviour_" + element.testBehaviour) + " (VFA)"}
          </Text>
        </Table.Td>
        <Table.Td>
          <Text>{t(element.eye)}</Text>
        </Table.Td>
        {showAllColumns && (
          <>
            <Table.Td>
              <Text>{displayClinicianLabel(element, ClinicianLabelField.Diagnosis, t)}</Text>
            </Table.Td>
            <Table.Td>
              <Text>{displayClinicianLabel(element, ClinicianLabelField.Reliability, t)}</Text>
            </Table.Td>
          </>
        )}
      </Table.Tr>
    ));

  return (
    <Box>
      <Group justify="space-between" mb={"sm"} align="center">
        {props.title}
        <Button
          variant="default"
          size="compact-sm"
          onClick={() => setShowAllColumns(!showAllColumns)}
          data-testid="selectable-test-list-toggle-columns"
        >
          {showAllColumns ? t("ResultList.showFewerColumns") : t("ResultList.showAllColumns")}
        </Button>
        {!props.alwaysShowFilter && props.filterable && (
          <Button
            color={showFilter ? "red.4" : "blue.4"}
            size="compact-sm"
            onClick={() => setShowFilter(!showFilter)}
          >
            {showFilter ? t("OpaReports.close") : t("OpaReports.filter")}
          </Button>
        )}

        {props.selectable && !props.baselinSet && (
          <Button
            onClick={() => props.generateButton(selectedRows, baselineTestIdsFromCloud)}
            disabled={props.isGenerateDisabled ? props.isGenerateDisabled(selectedRows) : true}
          >
            {t("OpaReports.generate")}
          </Button>
        )}

        {props.baselinSet && (
          <Button
            onClick={() => {
              const body = {
                baselineTests: selectedRows,
                eye: eye,
                patientId: props.patientId,
                testType: testType,
              };
              console.log(body);
              setButtonLoading(true);
              setBaselineTests(
                body,
                (response) => {
                  console.log(response);
                  setButtonLoading(false);
                  getBaseline();
                },
                (error) => {
                  console.error(error);
                  setButtonLoading(false);
                },
              );
            }}
            disabled={
              selectedRows.length < 2 ||
              selectedRows.filter((row) => defaultSelected?.includes(row)).length == 2
            }
          >
            {buttonLoading ? <Loader /> : t("OpaReports.setBaseline")}
          </Button>
        )}
      </Group>

      <Stack align="center">
        {((props.filterable && showFilter) || props.alwaysShowFilter) && (
          <Group gap="md" grow w={"100%"}>
            <Select
              label={t("TestController.testType")}
              placeholder={t("TestController.testType")}
              data={testTypeDataFiltered}
              value={testType}
              onChange={(value) => setTestType(value)}
              clearable={props.defaultTestType ? false : true}
              allowDeselect={props.defaultTestType ? false : true}
            />
            <Select
              label={t("TestController.testBehaviour")}
              placeholder={t("TestController.testBehaviour")}
              data={testBehaviourDataFiltered}
              value={testBehaviour}
              onChange={(value) => setTestBehaviour(value)}
              clearable
            />
            <Select
              label={t("TestController.eye")}
              placeholder={t("TestController.eye")}
              data={[
                { value: "leftEye", label: t("TestController.leftEye") },
                { value: "rightEye", label: t("TestController.rightEye") },
              ]}
              value={eye}
              onChange={(value) => setEye(value)}
              clearable={props.defaultEye ? false : true}
              allowDeselect={props.defaultEye ? false : true}
            />
          </Group>
        )}
        <Box pos="relative" w={"100%"} mih={"300px"}>
          <LoadingOverlay
            visible={loading}
            zIndex={1000}
            overlayProps={{ radius: "sm", blur: 2 }}
          />
          <Stack align="center">
            {props.showSelectedOnTop === true &&
              (table(tests.filter((test) => selectedRows.includes(test.uuid))) || null)}
            {props.showSelectedOnTop === true && selectedRows.length == 0 && (
              <Text>{t("OpaReports.twoBaselineTestNeedsToBeSelected")}</Text>
            )}
            {props.showSelectedOnTop === true
              ? table(tests.filter((test) => !selectedRows.includes(test.uuid)))
              : table(tests)}
            {props.minimumTestNeededWarningText && props.minimumTestNeeded >= tests.length && (
              <Text c="red.6">{props.minimumTestNeededWarningText}</Text>
            )}
            <Pagination mt={"md"} value={page} onChange={setPage} total={totalPages} />
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}
